// src/components/Teammember.jsx

import React, { useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import teamMembers from './data/TeamData';
import './Teammember.css';
import config from '../config';
import SchemaMarkup from './SchemaMarkup';
import { getMemberBreadcrumbSchema } from '../schemas/teamSchema';

const BackArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M5 12l7-7M5 12l7 7" />
  </svg>
);

const ForwardArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M14 6l6 6-6 6" />
  </svg>
);

const LinkedInIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const TeamMember = () => {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const memberIndex = teamMembers.findIndex((m) => m.id === memberId);
  const member = teamMembers[memberIndex];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [memberId]);

  if (!member) {
    return (
      <div style={{ padding: '120px 64px', textAlign: 'center' }}>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32 }}>
          Member not found.
        </p>
        <Link to="/team" style={{ color: '#014293' }}>← Back to Team</Link>
      </div>
    );
  }

  const colleagues = teamMembers.filter((m) => m.id !== member.id);

  const handleContactClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    } else {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="tm-page">

      <SchemaMarkup
        pageId={`team-member-${member.id}`}
        schemas={[getMemberBreadcrumbSchema(member.name, member.id)]}
      />

      <Link to="/team" className="tm-back">
        <BackArrow /> Our Team
      </Link>

      <section className="tm-hero">
        <div className="tm-photo-panel">
          <div className="tm-photo-badge">
            {String(memberIndex + 1).padStart(2, '0')} / {String(teamMembers.length).padStart(2, '0')}
          </div>
          <img src={member.photo} alt={member.name} loading="eager" />
        </div>

        <div className="tm-info-panel">
          <h1 className="tm-name">{member.name}</h1>
          <p className="tm-title">{member.title}</p>
          <div className="tm-divider" />
          <p className="tm-short-bio">{member.shortBio}</p>

          <div className="tm-meta-row">
            <div className="tm-meta-item">
              <span className="tm-meta-label">Experience</span>
              <span className="tm-meta-value">{member.yearsExp}</span>
              <span className="tm-meta-label" style={{ marginTop: 2 }}>Years</span>
            </div>
            <div className="tm-meta-item tm-meta-location">
              <span className="tm-meta-label">Based in</span>
              <div className="tm-location-row">
                <span className="tm-meta-value">{member.location}</span>
                <a
                  href={member.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tm-linkedin"
                >
                  <LinkedInIcon /> Connect on LinkedIn
                </a>
              </div>
            </div>
          </div>

          <div className="tm-industries-hero">
            <span className="tm-industries-label">Key Industries</span>
            <div className="tm-industries-tags">
              {member.industries.map((ind) => (
                <span key={ind} className="tm-industry-tag">{ind}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="tm-colleagues">
        <div className="tm-colleagues-header">
          <h3>Also on the team</h3>
          <Link to="/team" className="tm-see-all">
            View all advisors <ForwardArrow />
          </Link>
        </div>
        <div className="tm-colleagues-row">
          {colleagues.map((col) => (
            <Link key={col.id} to={`/team/${col.id}`} className="tm-colleague-card">
              <div className="tm-colleague-photo">
                <img src={col.photo} alt={col.name} loading="lazy" />
              </div>
              <div className="tm-colleague-info">
                <p className="tm-colleague-name">{col.name}</p>
                <p className="tm-colleague-title">{col.title}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="tm-cta">
        <div
          className="tm-cta-bg"
          style={{ backgroundImage: `url(${config.imageBaseURL}${config.paths.banners}uae-cfobridge2.webp)` }}
        />
        <div className="tm-cta-overlay" />
        <div className="tm-cta-inner">
          <h2>Ready to elevate your finance function?</h2>
          <p>
            Let CFO Bridge UAE become the strategic financial partner your business deserves —
            on-demand expertise, real ROI, zero full-time overhead.
          </p>
          <a href="#contact" className="tm-btn-cta" onClick={handleContactClick}>
            Schedule Consultation
          </a>
        </div>
      </section>

    </div>
  );
};

export default TeamMember;